import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Sparkles, Sun, Moon, Star, Compass, Mail, Lock, User, ArrowRight, Loader2, Eye, EyeOff } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, setUser, storeToken } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.onboarding_complete) {
        navigate("/dashboard", { replace: true });
      } else {
        navigate("/onboarding", { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate]);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    if (!isLogin && !name.trim()) {
      toast.error("Please enter your name");
      return;
    }

    setLoading(true);
    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const payload = isLogin ? { email, password } : { email, password, name };
      const res = await api.post(endpoint, payload);
      const userData = res.data.user;
      const token = res.data.token;
      storeToken(token);
      setUser(userData);
      toast.success(isLogin ? "Welcome back!" : "Account created!");
      if (userData.onboarding_complete) {
        navigate("/dashboard", { replace: true });
      } else {
        navigate("/onboarding", { replace: true });
      }
    } catch (err) {
      console.error("Auth error:", err);
      const detail = err.response?.data?.detail;
      let msg;
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail.map(d => d.msg || JSON.stringify(d)).join(". ");
      } else if (err.response?.status === 429) {
        msg = "Too many attempts. Please try again in 15 minutes.";
      } else if (err.response?.status === 401) {
        msg = "Invalid email or password. Please try again.";
      } else if (err.response?.status >= 500) {
        msg = "Server error. Please try again in a moment.";
      } else if (!err.response) {
        msg = "Network error. Please check your connection.";
      } else {
        msg = "Login failed. Please try again.";
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: '#070B14' }}>
      {/* Background */}
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/7736064/pexels-photo-7736064.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#070B14]/80 via-[#070B14]/60 to-[#070B14]" />

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="p-6 md:p-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A16207] flex items-center justify-center">
              <Compass className="w-5 h-5 text-[#070B14]" />
            </div>
            <span className="font-display text-2xl text-white">SoulGuide</span>
          </div>
        </header>

        {/* Main */}
        <main className="flex-1 flex flex-col items-center justify-center px-6 py-8 text-center">
          {!showAuth ? (
            /* Hero Section */
            <div className="max-w-2xl mx-auto space-y-8 animate-fade-in-up">
              <div className="flex justify-center gap-6 mb-8">
                <Sun className="w-8 h-8 text-[#D4AF37] animate-pulse" />
                <Star className="w-6 h-6 text-[#C4B5FD]" />
                <Moon className="w-8 h-8 text-[#8B5CF6]" />
              </div>

              <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] font-body font-medium">
                Daily Wisdom for Real Life
              </p>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-tight">
                Your Personal Guide to{" "}
                <span className="gold-text">Clarity</span> &{" "}
                <span className="gold-text">Purpose</span>
              </h1>

              <p className="font-body font-light text-lg text-white/70 max-w-lg mx-auto leading-relaxed">
                Receive personalised daily guidance, spiritual insights, and reflective prompts
                to navigate each day with intention and peace.
              </p>

              <div className="flex flex-wrap justify-center gap-4 py-6">
                {["Daily Guidance", "AI Tarot", "Wellness Tracking", "Chat Advisor"].map((feature) => (
                  <div
                    key={feature}
                    className="px-4 py-2 rounded-full border border-white/10 bg-white/5 text-sm text-white/80 font-body"
                  >
                    {feature}
                  </div>
                ))}
              </div>

              <div className="pt-4 space-y-4">
                <button
                  data-testid="get-started-btn"
                  onClick={() => setShowAuth(true)}
                  className="btn-gold inline-flex items-center gap-2 text-lg font-medium animate-pulse-glow"
                >
                  <Sparkles className="w-5 h-5" />
                  Begin Your Journey
                </button>
              </div>
            </div>
          ) : (
            /* Auth Form */
            <div className="w-full max-w-md mx-auto animate-fade-in-up">
              <div className="glass-card p-8 space-y-6">
                <div className="text-center">
                  <h2 className="font-display text-2xl text-white">
                    {isLogin ? "Welcome Back" : "Create Account"}
                  </h2>
                  <p className="text-white/50 font-body text-sm mt-1">
                    {isLogin ? "Sign in to continue your journey" : "Start your spiritual journey today"}
                  </p>
                </div>

                {/* Google OAuth */}
                <button
                  data-testid="google-login-btn"
                  onClick={handleGoogleLogin}
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white font-body hover:bg-white/15 transition-colors"
                >
                  <svg width="20" height="20" viewBox="0 0 48 48">
                    <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.9 33.3 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.2-2.7-.4-3.9z"/>
                    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 15.5 18.8 12 24 12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.1 35.2 26.7 36 24 36c-5.3 0-9.8-3.6-11.3-8.5l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
                    <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.6 39.6 44 34 44 24c0-1.3-.2-2.7-.4-3.9z"/>
                  </svg>
                  Continue with Google
                </button>

                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-white/10" />
                  <span className="text-white/30 text-xs font-body">or</span>
                  <div className="flex-1 h-px bg-white/10" />
                </div>

                {/* Email Auth Form */}
                <form onSubmit={handleEmailAuth} className="space-y-4">
                  {!isLogin && (
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                      <input
                        data-testid="register-name"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your name"
                        className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white font-body text-sm placeholder-white/30 focus:outline-none focus:border-[#D4AF37]/50"
                      />
                    </div>
                  )}
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                    <input
                      data-testid="email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Email address"
                      className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white font-body text-sm placeholder-white/30 focus:outline-none focus:border-[#D4AF37]/50"
                    />
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                    <input
                      data-testid="password-input"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Password"
                      className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-12 py-3 text-white font-body text-sm placeholder-white/30 focus:outline-none focus:border-[#D4AF37]/50"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="btn-gold w-full py-5 text-base"
                    data-testid="email-auth-submit"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        {isLogin ? "Sign In" : "Create Account"}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>

                {/* Toggle login/register */}
                <div className="text-center space-y-2">
                  {isLogin && (
                    <button
                      onClick={() => navigate("/forgot-password")}
                      className="text-white/40 hover:text-[#D4AF37] font-body text-sm transition-colors"
                      data-testid="forgot-password-link"
                    >
                      Forgot your password?
                    </button>
                  )}
                  <p className="text-white/40 text-sm font-body">
                    {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
                    <button
                      onClick={() => setIsLogin(!isLogin)}
                      className="text-[#D4AF37] hover:underline font-medium"
                      data-testid="toggle-auth-mode"
                    >
                      {isLogin ? "Sign Up" : "Sign In"}
                    </button>
                  </p>
                </div>
              </div>

              <button
                onClick={() => setShowAuth(false)}
                className="text-white/40 hover:text-white/60 font-body text-sm mt-4 block mx-auto"
              >
                Back to Home
              </button>
            </div>
          )}
        </main>

        <footer className="p-6 text-center">
          <p className="text-xs text-white/30 font-body">
            SoulGuide provides reflective, motivational, and spiritual lifestyle guidance for personal inspiration only.
            It does not replace medical, legal, mental health, or financial advice.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default LandingPage;
